import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Info, User, FilmStrip } from "@phosphor-icons/react";

export default function About() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9F6F0] to-white">
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[#1A1D1A] mb-3">
            About
          </h1>
          <p className="text-lg text-[#5C665F]">
            Learn more about our project, community, and vision
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-[#E5E0D8] shadow-lg overflow-hidden">
          <Tabs defaultValue="app" className="w-full">
            <TabsList className="w-full grid grid-cols-3 bg-[#F9F6F0] rounded-none border-b border-[#E5E0D8]">
              <TabsTrigger
                value="app"
                className="flex items-center gap-2 data-[state=active]:text-[#C86B4E] data-[state=active]:border-b-2 data-[state=active]:border-[#C86B4E]"
              >
                <Info size={18} />
                <span className="hidden sm:inline">App</span>
              </TabsTrigger>
              <TabsTrigger
                value="developer"
                className="flex items-center gap-2 data-[state=active]:text-[#C86B4E] data-[state=active]:border-b-2 data-[state=active]:border-[#C86B4E]"
              >
                <User size={18} />
                <span className="hidden sm:inline">Developer</span>
              </TabsTrigger>
              <TabsTrigger
                value="content"
                className="flex items-center gap-2 data-[state=active]:text-[#C86B4E] data-[state=active]:border-b-2 data-[state=active]:border-[#C86B4E]"
              >
                <FilmStrip size={18} />
                <span className="hidden sm:inline">Content</span>
              </TabsTrigger>
            </TabsList>

            {/* About the App */}
            <TabsContent value="app" className="p-8 sm:p-12">
              <h2 className="font-display font-bold text-2xl text-[#1A1D1A] mb-4">
                About Discover By Bike
              </h2>
              <div className="space-y-4 text-[#5C665F] leading-relaxed">
                <p>
                  <strong className="text-[#1A1D1A]">Discover By Bike</strong> is a curated platform dedicated to
                  showcasing hand-selected bikepacking routes paired with high-quality films, GPX tracks,
                  and synchronized elevation profiles.
                </p>
                <p>
                  Our mission is to inspire riders by combining stunning cinematography with practical route
                  data, allowing you to experience routes from the comfort of home before embarking on your
                  own adventure.
                </p>
                <div className="bg-[#F9F6F0] p-6 rounded-xl mt-6 border border-[#E5E0D8]">
                  <h3 className="font-bold text-[#1A1D1A] mb-3">Key Features</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-[#C86B4E] font-bold">•</span>
                      <span>Curated bikepacking routes with detailed GPX files</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#C86B4E] font-bold">•</span>
                      <span>Synchronized elevation profiles and rider-shot films</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#C86B4E] font-bold">•</span>
                      <span>Interactive map with points of interest (water sources, repairs, food, etc.)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#C86B4E] font-bold">•</span>
                      <span>Save your favorite routes for easy access</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#C86B4E] font-bold">•</span>
                      <span>Community-driven content with credits to creators</span>
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            {/* About the Developer */}
            <TabsContent value="developer" className="p-8 sm:p-12">
              <h2 className="font-display font-bold text-2xl text-[#1A1D1A] mb-4">
                About the Developer
              </h2>
              <div className="space-y-4 text-[#5C665F] leading-relaxed">
                <p>
                  <strong className="text-[#1A1D1A]">Discover By Bike</strong> was created by Brendan Fosberry, a passionate
                  software engineer and biking enthusiast who wanted to share the beauty and challenge
                  of long-distance cycling with the world.
                </p>
                <p>
                 Brendan works at Render, which is where the backend for this app is hosted. He has a background in building scalable web applications and a love for the outdoors, 
                 which inspired him to create a platform that combines both.
                </p>
                <p>
                  The project combines a love for technology, outdoor adventure, and storytelling to create
                  a platform that bridges the gap between planning and experiencing bikepacking routes.
                </p>
                <div className="bg-[#F9F6F0] p-6 rounded-xl mt-6 border border-[#E5E0D8]">
                  <h3 className="font-bold text-[#1A1D1A] mb-3">Tech Stack</h3>
                  <p className="text-sm mb-3">
                    Built with modern web technologies for reliability and performance:
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-[#C86B4E] font-bold">•</span>
                      <span><strong>Frontend:</strong> React with Tailwind CSS and Radix UI</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#C86B4E] font-bold">•</span>
                      <span><strong>Backend:</strong> Go with chi router and PostgreSQL</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#C86B4E] font-bold">•</span>
                      <span><strong>Maps:</strong> Leaflet.js for interactive route visualization</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#C86B4E] font-bold">•</span>
                      <span><strong>Charts:</strong> Recharts for elevation profile rendering</span>
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            {/* About the Content */}
            <TabsContent value="content" className="p-8 sm:p-12">
              <h2 className="font-display font-bold text-2xl text-[#1A1D1A] mb-4">
                About the Content
              </h2>
              <div className="space-y-4 text-[#5C665F] leading-relaxed">
                <p>
                  Each route on <strong className="text-[#1A1D1A]">Discover By Bike</strong> is carefully
                  selected and includes contributions from multiple creators—filmmakers, riders, and
                  route designers who share their passion and expertise.
                </p>
                <p>
                  Their content represents diverse riding styles, terrains, and experiences, from high alpine
                  bikepacking to desert explorers and everything in between. We aim to provide these creators
                  with a platform to showcase their work in new and engaging ways.
                </p>
                <p>
                  Discover by Bike does not host or own any of the video or GPS data, and tries to surface creators
                  content in a new way, keeping it attributed to the original creators. As such all videos are embedded
                  from sources like Youtube, where creators have control over their content. We are grateful to the 
                  community of riders and filmmakers who share their work and inspire us all.
                </p>
                <div className="bg-[#F9F6F0] p-6 rounded-xl mt-6 border border-[#E5E0D8]">
                  <h3 className="font-bold text-[#1A1D1A] mb-3">What You'll Find</h3>
                  <ul className="space-y-3 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-[#C86B4E] font-bold">📍</span>
                      <div>
                        <strong>Routes:</strong> Hand-selected bikepacking routes with full GPX files and
                        elevation data
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#C86B4E] font-bold">🎬</span>
                      <div>
                        <strong>Films:</strong> Professional and amateur-shot rider perspectives from the
                        field
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#C86B4E] font-bold">💧</span>
                      <div>
                        <strong>Points of Interest:</strong> Water sources, bike repair shops, restaurants,
                        scenic viewpoints, and more
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#C86B4E] font-bold">👥</span>
                      <div>
                        <strong>Creator Credits:</strong> Learn about the filmmakers, riders, and designers
                        behind each route
                      </div>
                    </li>
                  </ul>
                </div>
                <p className="mt-6 text-sm italic">
                  We believe in celebrating the community of creators who make bikepacking possible—from
                  route designers to filmmakers to the riders who inspire us all.
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
